import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IAppState } from '../app/app-state';
import { Action, Store } from '@ngrx/store';
import { PayrollDataService } from '@core/data/api/payroll/payroll-data.service';
import { mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import {
    EMPLOYEE_CREATE,
    EMPLOYEE_BENEFITS_FETCH,
    EMPLOYEE_BENEFITS_FETCH_MORE,
    EMPLOYEE_DEDUCTIONS_FETCH,
    EMPLOYEE_DEDUCTIONS_FETCH_MORE,
    EMPLOYEE_DOCUMENT_DOWNLOAD,
    EMPLOYEE_DOCUMENTS_FETCH,
    EMPLOYEE_DOCUMENTS_FETCH_MORE,
    EMPLOYEE_EXCLUDE,
    EMPLOYEE_FETCH,
    EMPLOYEE_PAYSTUB_DOWNLOAD,
    EMPLOYEE_PAYSTUBS_FETCH,
    EMPLOYEE_PAYSTUBS_FETCH_MORE,
    EMPLOYEES_FETCH,
    EMPLOYEES_SUGGESTIONS_FETCH,
    ROUTER_NAVIGATION,
    EMPLOYEE_BENEFITS_MANAGEMENT_LAUNCH,
    EMPLOYEE_DEDUCTIONS_MANAGEMENT_LAUNCH,
    EMPLOYEE_PATCH,
    EMPLOYEE_UPDATE,
    EMPLOYEE_INVITE,
    EMPLOYEE_RESTART,
    EMPLOYEE_TERMINATE,
    EMPLOYEE_WAGE_UPDATE,
    EMPLOYEE_WAGE_DELETE_PENDING,
    EMPLOYEE_WAGES_FETCH,
    EMPLOYEES_PATCH
} from '../action-types';
import {
    EMPLOYEE_BENEFIT_ENTITY,
    EMPLOYEE_DEDUCTION_ENTITY,
    EMPLOYEE_DOCUMENT_ENTITY,
    EMPLOYEE_ENTITY,
    EMPLOYEE_PAYSTUB_ENTITY,
    EMPLOYEE_WAGE_ENTITY
} from '../entity-types';
import { ossNormalize } from '@core/operators/normalize/normalize.operator';
import { switchMap } from 'rxjs/operators';
import {
    CreateEmployeeAction,
    ExcludeEmployeeAction,
    FetchEmployeeAction,
    FetchEmployeePaystubsAction,
    FetchEmployeesAction,
    ResetEmployeesAction,
    SetEmployeeAction,
    SetEmployeesAction,
    SetEmployeesFetchingAction,
    SetViewedEmployeeAction,
    UnsetEmployeeAction,
    SetEmployeePaystubsAction,
    SetEmployeePaystubsRelationshipAction,
    SetEmployeePaystubsFetchingAction,
    DownloadEmployeePaystubAction,
    FetchMoreEmployeePaystubsAction,
    MergeEmployeePaystubsAction,
    SetEmployeePaystubsFetchingMoreAction,
    SetMoreEmployeePaystubsRelationshipAction,
    FetchEmployeeDocumentsAction,
    SetEmployeeDocumentsAction,
    SetEmployeeDocumentsRelationshipAction,
    SetEmployeeDocumentsFetchingAction,
    FetchMoreEmployeeDocumentsAction,
    SetEmployeeDocumentsFetchingMoreAction,
    SetMoreEmployeeDocumentsRelationshipAction,
    MergeEmployeeDocumentsAction,
    DownloadEmployeeDocumentAction,
    FetchEmployeeDeductionsAction,
    SetEmployeeDeductionsAction,
    SetEmployeeDeductionsRelationshipAction,
    SetEmployeeDeductionsFetchingAction,
    FetchMoreEmployeeDeductionsAction,
    SetMoreEmployeeDeductionsRelationshipAction,
    SetEmployeeDeductionsFetchingMoreAction,
    MergeEmployeeDeductionsAction,
    FetchEmployeeBenefitsAction,
    SetEmployeeBenefitsFetchingAction,
    SetEmployeeBenefitsRelationshipAction,
    SetEmployeeBenefitsAction,
    FetchMoreEmployeeBenefitsAction,
    SetMoreEmployeeBenefitsRelationshipAction,
    SetEmployeeBenefitsFetchingMoreAction,
    MergeEmployeeBenefitsAction,
    LaunchEmployeeBenefitsManagementAction,
    LaunchEmployeeDeductionsManagementAction,
    PatchEmployeeAction,
    UpdateEmployeeAction,
    SendEmployeeInvitationAction,
    RestartEmployeeAction,
    TerminateEmployeeAction,
    UpdateEmployeeWageAction,
    DeletePendingEmployeeWageAction,
    FetchEmployeeWagesAction,
    SetEmployeeWagesAction,
    SetEmployeeWagesFetchingAction,
    SetEmployeeWagesRelationshipAction,
    PatchEmployeesAction
} from './employees-actions';
import { FetchEmployeeSuggestionsAction } from '../settings/settings-actions';
import { NormalizeResponse } from '@core/operators/normalize/normalize-response';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { from } from 'rxjs';
import { Employee } from '@core/models/employee/employee';
import { IAppRouterState } from '../router/router-state';
import {
    EMPLOYEES,
    EMPLOYEES_CREATE,
    EMPLOYEES_DETAILS,
    EMPLOYEES_DETAILS_BENEFITS,
    EMPLOYEES_DETAILS_BENEFITS_MANAGE,
    EMPLOYEES_DETAILS_DEDUCTIONS,
    EMPLOYEES_DETAILS_DEDUCTIONS_MANAGE,
    EMPLOYEES_DETAILS_DOCUMENTS,
    EMPLOYEES_DETAILS_PAYSTUBS,
    EMPLOYEES_DETAILS_PROFILE,
    EMPLOYEES_DETAILS_PROFILE_START_DATE,
    EMPLOYEES_DETAILS_PROFILE_WORKPLACES,
    EMPLOYEES_INACTIVE,
    EMPLOYEES_INACTIVE_DETAILS_PROFILE,
    EMPLOYEES_INACTIVE_DETAILS_PROFILE_RESTART_DATE,
    EMPLOYEES_INACTIVE_DETAILS_PAYSTUBS,
    EMPLOYEES_INACTIVE_DETAILS_DOCUMENTS,
    EMPLOYEES_INACTIVE_DETAILS_BENEFITS,
    EMPLOYEES_INACTIVE_DETAILS_BENEFITS_MANAGE,
    EMPLOYEES_INACTIVE_DETAILS_DEDUCTIONS,
    EMPLOYEES_INACTIVE_DETAILS_DEDUCTIONS_MANAGE,
    NO_STATE,
    EMPLOYEES_DETAILS_WAGES,
    EMPLOYEES_FIND,
    EMPLOYEES_INACTIVE_DETAILS_WAGES,
    EMPLOYEES_LIST,
    EMPLOYEES_INACTIVE_LIST,
    EMPLOYEES_DETAILS_PROFILE_PAYSCHEDULE
} from '@core/router/app-states';
import { schema } from 'normalizr';
import { RouterNavigationCancelAction } from '../router/router-actions';
import { RouterGoAction } from '../router/router-actions';
import { Paystub } from '@core/models/shared/paystub/paystub';
import { Document } from '@core/models/shared/document/document';
import { EmployeeWage } from '@core/models/employee/employee-wage';
import {
    getEmployeeDocumentList,
    getEmployeeDeductionList,
    getEmployeeBenefitList,
    getEmployeePaystubList,
    getViewedEmployeeId
} from './employees-selectors';
import { Deduction } from '@core/models/employee/deduction/deduction';
import { Benefit } from '@core/models/employee/benefit/benefit';
import { CheckService } from '@core/services/check.service';
import { FetchWorkplacesAction } from '../workplaces/workplaces-actions';

@Injectable()
export class EmployeesEffects {

    constructor(private payrollDataService: PayrollDataService,
                private store$: Store<IAppState>,
                private checkService: CheckService,
                private actions$: Actions) {
    }

    fetchEmployees$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchEmployeesAction>(EMPLOYEES_FETCH),
            switchMap(
                (action: FetchEmployeesAction) => {

                    return this.payrollDataService.fetchEmployees(action.payload)
                        .pipe(
                            ossNormalize(EMPLOYEE_ENTITY, Employee, 'employees'),
                            mergeMap((normalized: NormalizeResponse) => {

                                return [
                                    new SetEmployeesAction(normalized),
                                    new SetEmployeesFetchingAction(false)
                                ];
                            }),
                            ossCatchError(([actions, error]) => from(actions.concat([new SetEmployeesFetchingAction(false)])))
                        );
                }
            )
        )
    );

    fetchEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchEmployeeAction>(EMPLOYEE_FETCH),
            switchMap((action: FetchEmployeeAction) => {
                return this.payrollDataService.fetchEmployee(action.payload.id)
                    .pipe(
                        mergeMap((response: any) => {

                            return [
                                new SetEmployeeAction({
                                    item: Employee.import(response),
                                    schema: new schema.Entity(EMPLOYEE_ENTITY)
                                })
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error(error)
                        ]))
                    );
            })
        )
    );

    fetchEmployeePaystubs$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchEmployeePaystubsAction>(EMPLOYEE_PAYSTUBS_FETCH),
            switchMap((action: FetchEmployeePaystubsAction) => {

                const employeeId = action.payload.id;

                return this.payrollDataService.fetchEmployeePaystubs(employeeId)
                    .pipe(
                        ossNormalize(EMPLOYEE_PAYSTUB_ENTITY, Paystub, 'paystubs'),
                        mergeMap((normalized: NormalizeResponse) => {

                            return [
                                new SetEmployeePaystubsAction(normalized),
                                new SetEmployeePaystubsRelationshipAction({
                                    itemIds: normalized.result,
                                    relatedId: employeeId
                                }),
                                new SetEmployeePaystubsFetchingAction(false)
                            ];
                        }),
                        ossCatchError(([actions, error]) => {
                            return from(actions.concat([new SetEmployeePaystubsFetchingAction(false)]))
                        })
                    );
            })
        )
    );

    fetchMoreEmployeePaystubs$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchMoreEmployeePaystubsAction>(EMPLOYEE_PAYSTUBS_FETCH_MORE),
            withLatestFrom(
                this.store$.select(getViewedEmployeeId),
                this.store$.select(getEmployeePaystubList)
            ),
            switchMap(([action, employeeId, paystubList]) => {
                return this.payrollDataService.fetchEmployeePaystubs(employeeId, paystubList.meta.next_cursor)
                .pipe(
                    ossNormalize(EMPLOYEE_PAYSTUB_ENTITY, Paystub, 'paystubs'),
                    mergeMap((normalized: NormalizeResponse) => {

                        return [
                            new MergeEmployeePaystubsAction(normalized),
                            new SetMoreEmployeePaystubsRelationshipAction({
                                itemIds: normalized.result,
                                relatedId: employeeId
                            }),
                            new SetEmployeePaystubsFetchingMoreAction(false)
                        ];
                    }),
                    ossCatchError(([actions, error]) => {
                        return from(actions.concat([new SetEmployeePaystubsFetchingMoreAction(false)]))
                    })
                );
            })
        )
    );

    downloadEmployeePaystub$ = createEffect(() =>
        this.actions$.pipe(
            ofType<DownloadEmployeePaystubAction>(EMPLOYEE_PAYSTUB_DOWNLOAD),

            switchMap((action: DownloadEmployeePaystubAction) => {
                return this.payrollDataService.downloadEmployeePaystub(action.payload.id, action.payload.paystubId)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([actions, error]) => from(actions.concat([(action as any).error(error)])))
                    )       
            })
        )
    );

    fetchEmployeeDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchEmployeeDocumentsAction>(EMPLOYEE_DOCUMENTS_FETCH),
            switchMap((action: FetchEmployeeDocumentsAction) => {

                const employeeId = action.payload.id;

                return this.payrollDataService.fetchEmployeeDocuments(employeeId)
                    .pipe(
                        ossNormalize(EMPLOYEE_DOCUMENT_ENTITY, Document, 'documents'),
                        mergeMap((normalized: NormalizeResponse) => {

                            return [
                                new SetEmployeeDocumentsAction(normalized),
                                new SetEmployeeDocumentsRelationshipAction({
                                    itemIds: normalized.result,
                                    relatedId: employeeId
                                }),
                                new SetEmployeeDocumentsFetchingAction(false)
                            ];
                        }),
                        ossCatchError(([actions, error]) => {
                            return from(actions.concat([new SetEmployeeDocumentsFetchingAction(false)]))
                        })
                    );
            })
        )
    );

    fetchMoreEmployeeDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchMoreEmployeeDocumentsAction>(EMPLOYEE_DOCUMENTS_FETCH_MORE),
            withLatestFrom(
                this.store$.select(getViewedEmployeeId),
                this.store$.select(getEmployeeDocumentList)
            ),
            switchMap(([action, employeeId, documentList]) => {
                return this.payrollDataService.fetchEmployeeDocuments(employeeId, documentList.meta.next_cursor)
                .pipe(
                    ossNormalize(EMPLOYEE_DOCUMENT_ENTITY, Document, 'documents'),
                    mergeMap((normalized: NormalizeResponse) => {

                        return [
                            new MergeEmployeeDocumentsAction(normalized),
                            new SetMoreEmployeeDocumentsRelationshipAction({
                                itemIds: normalized.result,
                                relatedId: employeeId
                            }),
                            new SetEmployeeDocumentsFetchingMoreAction(false)
                        ];
                    }),
                    ossCatchError(([actions, error]) => {
                        return from(actions.concat([new SetEmployeeDocumentsFetchingMoreAction(false)]))
                    })
                );
            })
        )
    );

    downloadEmployeeDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType<DownloadEmployeeDocumentAction>(EMPLOYEE_DOCUMENT_DOWNLOAD),

            switchMap((action: DownloadEmployeeDocumentAction) => {
                return this.payrollDataService.downloadEmployeeDocument(action.payload.id, action.payload.documentId)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([actions, error]) => from(actions.concat([(action as any).error(error)])))
                    )       
            })
        )
    );

    fetchEmployeeDeductions$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchEmployeeDeductionsAction>(EMPLOYEE_DEDUCTIONS_FETCH),
            switchMap((action: FetchEmployeeDeductionsAction) => {

                const employeeId = action.payload.id;

                return this.payrollDataService.fetchEmployeeDeductions(employeeId)
                    .pipe(
                        ossNormalize(EMPLOYEE_DEDUCTION_ENTITY, Deduction, 'deductions'),
                        mergeMap((normalized: NormalizeResponse) => {

                            return [
                                new SetEmployeeDeductionsAction(normalized),
                                new SetEmployeeDeductionsRelationshipAction({
                                    itemIds: normalized.result,
                                    relatedId: employeeId
                                }),
                                new SetEmployeeDeductionsFetchingAction(false)
                            ];
                        }),
                        ossCatchError(([actions, error]) => {
                            return from(actions.concat([new SetEmployeeDeductionsFetchingAction(false)]))
                        })
                    );
            })
        )
    );

    fetchMoreEmployeeDeductions$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchMoreEmployeeDeductionsAction>(EMPLOYEE_DEDUCTIONS_FETCH_MORE),
            withLatestFrom(
                this.store$.select(getViewedEmployeeId),
                this.store$.select(getEmployeeDeductionList)
            ),
            switchMap(([action, employeeId, deductionList]) => {
                return this.payrollDataService.fetchEmployeeDeductions(employeeId, deductionList.meta.next_cursor)
                .pipe(
                    ossNormalize(EMPLOYEE_DEDUCTION_ENTITY, Deduction, 'deductions'),
                    mergeMap((normalized: NormalizeResponse) => {

                        return [
                            new MergeEmployeeDeductionsAction(normalized),
                            new SetMoreEmployeeDeductionsRelationshipAction({
                                itemIds: normalized.result,
                                relatedId: employeeId
                            }),
                            new SetEmployeeDeductionsFetchingMoreAction(false)
                        ];
                    }),
                    ossCatchError(([actions, error]) => {
                        return from(actions.concat([new SetEmployeeDeductionsFetchingMoreAction(false)]))
                    })
                );
            })
        )
    );

    fetchEmployeeDeductionsManagementUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType<LaunchEmployeeDeductionsManagementAction>(EMPLOYEE_DEDUCTIONS_MANAGEMENT_LAUNCH),
            switchMap((action: LaunchEmployeeDeductionsManagementAction) => {
                return this.payrollDataService.fetchDeductionsManagementUrl(action.payload.id)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([actions, error]) => from(actions.concat([
                            (action as any).error(error)
                        ])))
                    )
            })
        )
    );

    fetchEmployeeBenefits$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchEmployeeBenefitsAction>(EMPLOYEE_BENEFITS_FETCH),
            switchMap((action: FetchEmployeeBenefitsAction) => {

                const employeeId = action.payload.id;

                return this.payrollDataService.fetchEmployeeBenefits(employeeId)
                    .pipe(
                        ossNormalize(EMPLOYEE_BENEFIT_ENTITY, Benefit, 'benefits'),
                        mergeMap((normalized: NormalizeResponse) => {

                            return [
                                new SetEmployeeBenefitsAction(normalized),
                                new SetEmployeeBenefitsRelationshipAction({
                                    itemIds: normalized.result,
                                    relatedId: employeeId
                                }),
                                new SetEmployeeBenefitsFetchingAction(false)
                            ];
                        }),
                        ossCatchError(([actions, error]) => {
                            return from(actions.concat([new SetEmployeeBenefitsFetchingAction(false)]))
                        })
                    );
            })
        )
    );

    fetchMoreEmployeeBenefits$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchMoreEmployeeBenefitsAction>(EMPLOYEE_BENEFITS_FETCH_MORE),
            withLatestFrom(
                this.store$.select(getViewedEmployeeId),
                this.store$.select(getEmployeeBenefitList)
            ),
            switchMap(([action, employeeId, benefitList]) => {
                return this.payrollDataService.fetchEmployeeBenefits(employeeId, benefitList.meta.next_cursor)
                .pipe(
                    ossNormalize(EMPLOYEE_BENEFIT_ENTITY, Benefit, 'benefits'),
                    mergeMap((normalized: NormalizeResponse) => {

                        return [
                            new MergeEmployeeBenefitsAction(normalized),
                            new SetMoreEmployeeBenefitsRelationshipAction({
                                itemIds: normalized.result,
                                relatedId: employeeId
                            }),
                            new SetEmployeeBenefitsFetchingMoreAction(false)
                        ];
                    }),
                    ossCatchError(([actions, error]) => {
                        return from(actions.concat([new SetEmployeeBenefitsFetchingMoreAction(false)]))
                    })
                );
            })
        )
    );

    fetchEmployeeBenefitsManagementUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType<LaunchEmployeeBenefitsManagementAction>(EMPLOYEE_BENEFITS_MANAGEMENT_LAUNCH),
            switchMap((action: LaunchEmployeeBenefitsManagementAction) => {
                return this.payrollDataService.fetchBenefitsManagementUrl(action.payload.id)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([actions, error]) => from(actions.concat([
                            (action as any).error(error)
                        ])))
                    )
            })
        )
    );

    fetchEmployeeWages$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchEmployeeWagesAction>(EMPLOYEE_WAGES_FETCH),
            switchMap((action: FetchEmployeeWagesAction) => {

                const employeeId = action.payload.id;

                return this.payrollDataService.fetchEmployeeWages(employeeId)
                    .pipe(
                        ossNormalize(EMPLOYEE_WAGE_ENTITY, EmployeeWage, 'wages'),
                        mergeMap((normalized: NormalizeResponse) => {

                            return [
                                new SetEmployeeWagesAction(normalized),
                                new SetEmployeeWagesRelationshipAction({
                                    itemIds: normalized.result,
                                    relatedId: employeeId
                                }),
                                new SetEmployeeWagesFetchingAction(false)
                            ];
                        }),
                        ossCatchError(([actions, error]) => {
                            return from(actions.concat([new SetEmployeeWagesFetchingAction(false)]))
                        })
                    );
            })
        )
    );

    createEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType<CreateEmployeeAction>(EMPLOYEE_CREATE),
            switchMap((action: CreateEmployeeAction) => {
                return this.payrollDataService.createEmployee(action.payload)
                    .pipe(
                        mergeMap((response: any) => {

                            const employee: Employee = Employee.import(response);

                            const actions: Action[] = [
                                new SetEmployeeAction({
                                    item: employee,
                                    schema: new schema.Entity(EMPLOYEE_ENTITY)
                                }),
                                (action as any).success(employee)
                            ];

                            if (action.payload.link_id) {
                                actions.push(new UnsetEmployeeAction({ id: action.payload.link_id }));
                            }

                            return actions;
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error(error)
                        ]))
                    )
            })
        )
    );

    updateEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateEmployeeAction>(EMPLOYEE_UPDATE),
            switchMap((action: UpdateEmployeeAction) => {
                return this.payrollDataService.updateEmployee(action.payload.id, action.payload)
                    .pipe(
                        mergeMap((response: any) => {

                            const employee: Employee = Employee.import(response);

                            const actions: Action[] = [
                                new SetEmployeeAction({
                                    item: employee,
                                    schema: new schema.Entity(EMPLOYEE_ENTITY)
                                }),
                                (action as any).success(employee)
                            ];

                            return actions;
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error(error)
                        ]))
                    )
            })
        )
    );

    patchEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType<PatchEmployeeAction>(EMPLOYEE_PATCH),
            switchMap((action: PatchEmployeeAction) => {
                return this.payrollDataService.patchEmployee(action.payload.id, action.payload)
                    .pipe(
                        mergeMap((response: any) => {

                            const employee: Employee = Employee.import(response);

                            const actions: Action[] = [
                                new SetEmployeeAction({
                                    item: employee,
                                    schema: new schema.Entity(EMPLOYEE_ENTITY)
                                }),
                                (action as any).success(employee)
                            ];

                            if (action.payload.link_id) {
                                actions.push(new UnsetEmployeeAction({ id: action.payload.link_id }));
                            }

                            return actions;
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error(error)
                        ]))
                    )
            })
        )
    );

    patchEmployees$ = createEffect(() =>
        this.actions$.pipe(
            ofType<PatchEmployeesAction>(EMPLOYEES_PATCH),
            switchMap((action: PatchEmployeesAction) => {
                return this.payrollDataService.patchEmployees(action.payload.employees)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                new FetchEmployeesAction(),
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    )
            })
        )
    );

    excludeEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType<ExcludeEmployeeAction>(EMPLOYEE_EXCLUDE),
            switchMap((action: ExcludeEmployeeAction) => {
                return this.payrollDataService.excludeEmployee(action.payload.id)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                new UnsetEmployeeAction({ id: action.payload.id }),
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    restartEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType<RestartEmployeeAction>(EMPLOYEE_RESTART),
            switchMap((action: RestartEmployeeAction) => {
                return this.payrollDataService.restartEmployee(action.payload.id, action.payload.startDate)
                        .pipe(
                            mergeMap((response: any) => {
                                const employee: Employee = Employee.import(response.employee);
    
                                const actions: Action[] = [
                                    new SetEmployeeAction({
                                        item: employee,
                                        schema: new schema.Entity(EMPLOYEE_ENTITY)
                                    }),
                                    (action as any).success(employee)
                                ];
    
                                return actions;
                            }),
                            ossCatchError(([errorActions, error]) => from([
                                (action as any).error(error)
                            ]))
                        );
                })
            )
        );

    terminateEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType<TerminateEmployeeAction>(EMPLOYEE_TERMINATE),
            switchMap((action: TerminateEmployeeAction) => {
                return this.payrollDataService.terminateEmployee(action.payload.id, action.payload.terminationDate)
                    .pipe(
                        mergeMap((response: any) => {
                            const employee: Employee = Employee.import(response.employee);

                            const actions: Action[] = [
                                new SetEmployeeAction({
                                    item: employee,
                                    schema: new schema.Entity(EMPLOYEE_ENTITY)
                                }),
                                (action as any).success(employee)
                            ];

                            return actions;
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error(error)
                        ]))
                    );
            })
        )
    );

    updateEmployeeWage$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateEmployeeWageAction>(EMPLOYEE_WAGE_UPDATE),
            switchMap((action: UpdateEmployeeWageAction) => {
                return this.payrollDataService.updateEmployeeWage(action.payload.id, action.payload.request)
                    .pipe(
                        ossNormalize(EMPLOYEE_WAGE_ENTITY, EmployeeWage, 'wages'),
                        mergeMap((normalized: NormalizeResponse) => {

                            return [
                                new SetEmployeeWagesAction(normalized),
                                new SetEmployeeWagesRelationshipAction({
                                    itemIds: normalized.result,
                                    relatedId: action.payload.id
                                }),
                                new FetchEmployeeAction({ id: action.payload.id }),
                                (action as any).success(normalized.data)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    deletePendingEmployeeWage$ = createEffect(() =>

        this.actions$.pipe(
            ofType<DeletePendingEmployeeWageAction>(EMPLOYEE_WAGE_DELETE_PENDING),
            switchMap((action: DeletePendingEmployeeWageAction) => {
                return this.payrollDataService.deletePendingEmployeeWage(action.payload.id)
                    .pipe(
                        ossNormalize(EMPLOYEE_WAGE_ENTITY, EmployeeWage, 'wages'),
                        mergeMap((normalized: NormalizeResponse) => {

                            return [
                                new SetEmployeeWagesAction(normalized),
                                new SetEmployeeWagesRelationshipAction({
                                    itemIds: normalized.result,
                                    relatedId: action.payload.id
                                }),
                                (action as any).success(normalized.data)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    sendEmployeeInvitation$ = createEffect(() =>
        this.actions$.pipe(
            ofType<SendEmployeeInvitationAction>(EMPLOYEE_INVITE),
            switchMap((action: SendEmployeeInvitationAction) => {
                return this.payrollDataService.sendEmployeeInvitation(action.payload.id)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    routerEmployees$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            mergeMap((action) => {
                const routerState: IAppRouterState = (action as any).payload
                .routerState as IAppRouterState;
                const { name: stateName, fromState, params } = routerState;
                const actions: Action[] = [];

                if (stateName === EMPLOYEES_INACTIVE_LIST) {
                    actions.push(new ResetEmployeesAction());
                    actions.push(new FetchEmployeesAction({ active: false }));

                } else if (stateName === EMPLOYEES_LIST) {
                    actions.push(new ResetEmployeesAction());
                    actions.push(new FetchEmployeesAction({ active: true }));

                } else  if (stateName === EMPLOYEES_CREATE) {

                    if (fromState.name !== EMPLOYEES_LIST && 
                        fromState.name !== EMPLOYEES_FIND &&
                        fromState.name !== EMPLOYEES_INACTIVE_LIST) {
                        actions.push(new RouterNavigationCancelAction({ state: EMPLOYEES_CREATE }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                        
                    }
                } else if (stateName === EMPLOYEES_FIND) {

                    if (fromState.name !== EMPLOYEES_LIST &&
                        fromState.name !== EMPLOYEES_INACTIVE_LIST) {
                        actions.push(new RouterNavigationCancelAction({ state: EMPLOYEES_FIND }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        actions.push(new SetViewedEmployeeAction({ id: null }));
                        actions.push(new FetchEmployeeSuggestionsAction());
                    }
                } else if (stateName === EMPLOYEES_DETAILS_PROFILE || stateName === EMPLOYEES_DETAILS) {
                    
                    if (fromState.name.indexOf(EMPLOYEES) === -1) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        actions.push(new FetchEmployeeAction({ id: params.id }));
                        actions.push(new SetViewedEmployeeAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_DETAILS_PROFILE_START_DATE) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    }
                } else if (stateName === EMPLOYEES_DETAILS_PROFILE_WORKPLACES) {
                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        actions.push(new FetchWorkplacesAction())
                    }
                } else if (stateName === EMPLOYEES_DETAILS_PROFILE_PAYSCHEDULE) {
                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    }
                } else if (stateName === EMPLOYEES_DETAILS_WAGES) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        actions.push(new FetchEmployeeWagesAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_DETAILS_PAYSTUBS) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        actions.push(new FetchEmployeePaystubsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_DETAILS_DOCUMENTS) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        actions.push(new FetchEmployeeDocumentsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_DETAILS_BENEFITS) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        actions.push(new FetchEmployeeBenefitsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_DETAILS_BENEFITS_MANAGE) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        //actions.push(new FetchEmployeeDeductionsAction({ id: params.id }));
                    }
                }  else if (stateName === EMPLOYEES_DETAILS_DEDUCTIONS) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        actions.push(new FetchEmployeeDeductionsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_DETAILS_DEDUCTIONS_MANAGE) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES }));
                    } else {
                        //actions.push(new FetchEmployeeDeductionsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_INACTIVE_DETAILS_PROFILE) {
                    
                    if (fromState.name.indexOf(EMPLOYEES_INACTIVE) === -1) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    } else {
                        actions.push(new FetchEmployeeAction({ id: params.id }));
                        actions.push(new SetViewedEmployeeAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_INACTIVE_DETAILS_PROFILE_RESTART_DATE) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    }
                } else if (stateName === EMPLOYEES_INACTIVE_DETAILS_WAGES) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    } else {
                        actions.push(new FetchEmployeeWagesAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_INACTIVE_DETAILS_PAYSTUBS) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    } else {
                        actions.push(new FetchEmployeePaystubsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_INACTIVE_DETAILS_DOCUMENTS) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    } else {
                        actions.push(new FetchEmployeeDocumentsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_INACTIVE_DETAILS_BENEFITS) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    } else {
                        actions.push(new FetchEmployeeBenefitsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_INACTIVE_DETAILS_BENEFITS_MANAGE) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    } else {
                        //actions.push(new FetchEmployeeDeductionsAction({ id: params.id }));
                    }
                }  else if (stateName === EMPLOYEES_INACTIVE_DETAILS_DEDUCTIONS) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    } else {
                        actions.push(new FetchEmployeeDeductionsAction({ id: params.id }));
                    }
                } else if (stateName === EMPLOYEES_INACTIVE_DETAILS_DEDUCTIONS_MANAGE) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: EMPLOYEES_INACTIVE }));
                    } else {
                        //actions.push(new FetchEmployeeDeductionsAction({ id: params.id }));
                    }
                } 

                return actions;
            })
        )
    );
}
